import { ReactElement, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  useTheme,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Star as StarIcon, CheckRounded } from "@mui/icons-material";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import {
  CheckoutError,
  useCreateCheckout,
  useMembershipStatus,
} from "../../hooks/useMembershipApi";
import {
  BillingInterval,
  PRICING_TIERS,
  PricingTier,
  formatPrice,
  formatPriceWhole,
  getTierByVariantId,
} from "../../data/pricingPlans";
import { MembershipStatus } from "../../types/lemonSqueezy";
import { authClient } from "../../lib/auth-client";
import { isNative } from "../../lib/platform";
import { useMembership } from "../../contexts/MembershipContext";
import { useAnalytics } from "../../hooks/useAnalytics";
import NativePaywall from "./NativePaywall";
import { ChangePlanDialog } from "../AccountSettings/ManagePlan";
import { useChatQuota } from "../Chat/hooks/useChatQuota";

const STALE_TAB_THRESHOLD_MS = 5 * 60 * 1000;

type PlanCardProps = {
  title: string;
  subtitle: string;
  priceLabel: string;
  priceUnit: string;
  caption: string;
  ctaLabel: string;
  buttonVariant: "outlined" | "contained";
  isLoading: boolean;
  isRevalidating: boolean;
  /** Absent for a member's current plan: the button shows, disabled. */
  onSelect?: () => void;
  featured?: boolean;
  badge?: string;
  badgeIcon?: ReactElement;
  savingsChip?: string;
  features?: string[];
};

const PlanCard = ({
  title,
  subtitle,
  priceLabel,
  priceUnit,
  caption,
  ctaLabel,
  buttonVariant,
  isLoading,
  isRevalidating,
  onSelect,
  featured = false,
  badge,
  badgeIcon,
  savingsChip,
  features,
}: PlanCardProps) => {
  const theme = useTheme();
  const isContained = buttonVariant === "contained";
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: { xs: 2.5, sm: 3 },
        position: "relative",
        // 1px everywhere (a 2px featured border read as heavy next to a 1px
        // sibling); the featured card is distinguished by border colour plus a
        // whisper of brand tint instead.
        border: featured
          ? `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        backgroundColor: featured
          ? isDark
            ? "rgba(34, 197, 94, 0.04)"
            : "rgba(34, 197, 94, 0.04)"
          : isDark
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(0, 0, 0, 0.02)",
        transition: "border-color 0.2s ease",
        "&:hover": {
          borderColor: featured
            ? theme.palette.primary.dark
            : isDark
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      {badge && (
        <Chip
          icon={badgeIcon}
          label={badge}
          size="small"
          sx={{
            position: "absolute",
            top: -11,
            left: 20,
            height: 22,
            fontWeight: 700,
            fontSize: "0.68rem",
            letterSpacing: "0.02em",
            px: 0.5,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        />
      )}

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "1.05rem",
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.25, fontSize: "0.8rem" }}
      >
        {subtitle}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 0.75,
          mt: 2.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "2rem",
            lineHeight: 1,
            letterSpacing: "-0.02em",
            // Price colour is the hierarchy: brand green is reserved for the
            // featured plan so the eye lands there first.
            color: featured
              ? theme.palette.primary.main
              : theme.palette.text.primary,
          }}
        >
          {priceLabel}
        </Typography>
        <Typography
          component="span"
          color="text.secondary"
          sx={{ fontSize: "0.85rem" }}
        >
          {priceUnit}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.72rem" }}
        >
          {caption}
        </Typography>
        {savingsChip && (
          <Typography
            component="span"
            sx={{
              fontSize: "0.66rem",
              fontWeight: 700,
              lineHeight: 1,
              px: 0.75,
              py: 0.4,
              borderRadius: 1,
              color: isDark ? "#22c55e" : "#16a34a",
              backgroundColor: "rgba(34, 197, 94, 0.12)",
              whiteSpace: "nowrap",
            }}
          >
            {savingsChip}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          my: 2.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mb: 3,
        }}
      >
        {features?.map((feature) => (
          <Box
            key={feature}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
          >
            <CheckRounded
              sx={{
                fontSize: 16,
                mt: "1px",
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button
        variant={buttonVariant}
        size="medium"
        fullWidth
        onClick={onSelect}
        disabled={isLoading || isRevalidating || !onSelect}
        sx={{
          py: { xs: 0.75, sm: 1, md: 1.25 },
          fontSize: { xs: "0.78rem", sm: "0.88rem", md: "0.95rem" },
          fontWeight: isContained ? 700 : 600,
          borderRadius: 2,
          textTransform: "none",
          ...(isContained && {
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }),
        }}
      >
        {isRevalidating && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress
              size={18}
              color={isContained ? "inherit" : undefined}
            />
            <span>Verifying session...</span>
          </Box>
        )}
        {!isRevalidating && isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress
              size={18}
              color={isContained ? "inherit" : undefined}
            />
            <span>Redirecting...</span>
          </Box>
        )}
        {!isRevalidating && !isLoading && ctaLabel}
      </Button>
    </Paper>
  );
};

/**
 * A cancelled Basic plan that is running out has to be reactivated before it
 * can move to Pro (change-plan needs an active subscription), so it gets no
 * purchase action. Every Basic member is Lemon Squeezy-billed: native sells
 * Pro only, so there is no store-billed Basic to guide.
 */
const EndingPlanGuidance = ({
  membership,
}: {
  membership: NonNullable<MembershipStatus["membership"]>;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const endDate = membership.expiresAt
    ? dayjs(membership.expiresAt).format("D MMMM YYYY")
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 520,
        mx: "auto",
        mb: { xs: 2.5, md: 3 },
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        textAlign: "center",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: isDark
          ? "rgba(255, 255, 255, 0.02)"
          : "rgba(0, 0, 0, 0.02)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
        {endDate && `Your Basic plan ends on ${endDate}`}
        {!endDate && "Your Basic plan is set to end"}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, mb: 2.5, lineHeight: 1.6 }}
      >
        Reactivate it from Account settings, then you can upgrade to Pro here.
      </Typography>
      <Button
        variant="outlined"
        onClick={() => navigate("/auth/settings")}
        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
      >
        Go to Account settings
      </Button>
    </Paper>
  );
};

/**
 * Pricing surface. On native the paywall is its own standalone component
 * (also rendered by the hard membership gate); the web keeps the three-card
 * Lemon Squeezy checkout page below. isNative() is constant for the app's
 * lifetime, so branching before hooks is safe.
 *
 * On web, every "Upgrade to Pro" in the app lands here. A Pro member is sent
 * to the dashboard. A Basic member sees the plans with an upgrade through the
 * existing plan change (immediate, prorated, never a second checkout), or
 * reactivation guidance when their plan is ending. Non-members get the
 * checkout.
 */
export const StartFreeTrialPage = () => {
  if (isNative()) return <NativePaywall />;
  return <WebStartFreeTrialPage />;
};

const WebStartFreeTrialPage = () => {
  const theme = useTheme();
  const { isMembershipActive } = useMembership();
  const [interval, setInterval] = useState<BillingInterval>("yearly");
  const [loadingPlan, setLoadingPlan] = useState<PricingTier["key"] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [updatePaymentUrl, setUpdatePaymentUrl] = useState<string | null>(null);
  const [lastActiveTime, setLastActiveTime] = useState<number>(Date.now());
  const [isRevalidating, setIsRevalidating] = useState(false);

  const createCheckoutMutation = useCreateCheckout();
  const { data: membershipStatus, isLoading: membershipLoading } =
    useMembershipStatus();
  const { capture } = useAnalytics();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromScreener = searchParams.get("from") === "screener";
  const quota = useChatQuota();
  const [upgradeTarget, setUpgradeTarget] = useState<PricingTier["key"] | null>(
    null,
  );
  const [upgraded, setUpgraded] = useState(false);

  const revalidateSession = useCallback(async () => {
    const timeSinceActive = Date.now() - lastActiveTime;
    if (timeSinceActive < STALE_TAB_THRESHOLD_MS) return;

    setIsRevalidating(true);
    try {
      const session = await authClient.getSession({
        fetchOptions: {
          onError: () => {
            window.location.href = "/sign-in?reason=session_expired";
          },
        },
      });
      if (!session?.data?.session) {
        window.location.href = "/sign-in?reason=session_expired";
        return;
      }
      setLastActiveTime(Date.now());
    } catch {
      window.location.href = "/sign-in?reason=session_expired";
    } finally {
      setIsRevalidating(false);
    }
  }, [lastActiveTime]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        revalidateSession();
      } else {
        setLastActiveTime(Date.now());
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [revalidateSession]);

  // Funnel: a (free-tier) user reached the pricing page — the step between the demo
  // and checkout_started. `source` comes from a ?from= link param when present.
  useEffect(() => {
    const source =
      new URLSearchParams(window.location.search).get("from") ?? "direct";
    capture("pricing_page_viewed", { source });
  }, [capture]);

  // Back-button from the LS checkout: the browser restores this page from the
  // back/forward cache with its React state frozen — including the loadingPlan
  // set just before the redirect — leaving the CTA stuck on "Redirecting…".
  // pageshow with persisted=true is exactly that restore signal; reset so every
  // plan is immediately buyable again.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoadingPlan(null);
        setError(null);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const membership = membershipStatus?.membership ?? null;
  // The server's plan, which covers both billing sources. If it can't load,
  // the variant decides, with unknown variants read as Pro (ManagePlan's
  // fail-safe), so the fallback is the old redirect.
  const planKey = quota.isPending
    ? undefined
    : (quota.data?.credits?.planKey ??
      getTierByVariantId(membership?.variantId)?.tier.key ??
      "pro");

  if (isMembershipActive && upgraded) return null;
  if (isMembershipActive && (membershipLoading || planKey === undefined)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (isMembershipActive && (planKey === "pro" || !membership)) {
    return <Navigate to="/dashboard" replace />;
  }

  const planEnding =
    isMembershipActive && membership?.isInGracePeriod === true;
  // A plan change keeps the member's billing term, so their prices show on it.
  const shownInterval: BillingInterval = isMembershipActive
    ? (getTierByVariantId(membership?.variantId)?.interval ?? "monthly")
    : interval;

  const handleUpgraded = () => {
    setUpgraded(true);
    enqueueSnackbar(
      "You're now on Pro. Unlimited screening, weekly AI chat and reports are active.",
      { variant: "success", autoHideDuration: 5000 },
    );
    // Back to where the upgrade started (a screener run resumes from its URL).
    const depth = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (depth > 0) navigate(-1);
    else navigate(fromScreener ? "/screener" : "/dashboard", { replace: true });
  };

  let subtitle =
    "Join hundreds of other investors who utilise Insight for their research.";
  if (isMembershipActive)
    subtitle =
      "You're on Basic. Pro adds unlimited screening, saved screens and weekly AI.";
  if (fromScreener)
    subtitle =
      "Pro gives you unlimited screening, up to 10 saved screens and CSV export.";

  // A customer who has ever held a membership (expired subscription, refunded
  // lifetime) has used their one free trial — the API creates their checkout with
  // skip_trial, so the CTA must promise a paid subscription, not another trial.
  // First-time customers receive the configured trial on either Basic or Pro.
  const hasPriorMembership = !!membershipStatus?.membership;

  const ctaLabelFor = (tier: PricingTier): string => {
    if (isMembershipActive)
      return tier.key === "pro" ? "Upgrade to Pro" : "Your current plan";
    if (tier.hasTrial && !hasPriorMembership) return "Start 7 day free trial";
    return `Get ${tier.name}`;
  };

  // Members never reach the checkout: one subscription per user, so Basic
  // moves to Pro through the plan change.
  const selectFor = (tier: PricingTier): (() => void) | undefined => {
    if (!isMembershipActive) return () => handleSelectPlan(tier);
    if (tier.key === "pro") return () => setUpgradeTarget("pro");
    return undefined;
  };

  const handleSelectPlan = async (tier: PricingTier) => {
    setLoadingPlan(tier.key);
    setError(null);
    setUpdatePaymentUrl(null);

    try {
      const session = await authClient.getSession();
      if (!session?.data?.session) {
        window.location.href = "/sign-in?reason=session_expired";
        return;
      }

      const variantId = tier.variantIds[interval];
      if (!variantId) {
        throw new Error("Variant ID not configured");
      }

      capture("checkout_started", {
        plan_type: `${tier.key}_${interval}`,
        variant_id: variantId,
      });

      const result = await createCheckoutMutation.mutateAsync({
        variantId,
        name: undefined,
      });

      const checkoutUrl = result.data.checkoutUrl;
      const lemonSqueezy = (window as any).LemonSqueezy;
      const finalUrl = lemonSqueezy?.Url?.Build?.(checkoutUrl) ?? checkoutUrl;

      window.location.href = finalUrl;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to start checkout";
      setError(errorMessage);
      setUpdatePaymentUrl((error as CheckoutError).updatePaymentMethodUrl ?? null);
      capture("checkout_failed", {
        plan_type: `${tier.key}_${interval}`,
        error_message: errorMessage,
      });
      setLoadingPlan(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // Centre vertically only where it fits (desktop). On mobile the stacked
        // cards are taller than the viewport, so top-align and let it scroll —
        // centring inside an over-tall box is what left the big gap up top.
        justifyContent: { xs: "flex-start", md: "center" },
        minHeight: { xs: "auto", md: "100vh" },
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 5, md: 4 },
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: 800, md: 1080 },
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              mb: { xs: 1.5, sm: 2, md: 3 },
              // Hidden on mobile/tablet — the app top bar already shows the logo
              // there, so rendering it again duplicated the branding.
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src="/insightLongLogoPrimaryWhiteText.svg"
              alt="Insight Analytics Logo"
              sx={{
                height: { xs: 32, sm: 40, md: 56 },
                width: "auto",
                maxWidth: { xs: "200px", sm: "240px", md: "360px" },
              }}
            />
          </Box>

          {/* Neutral headline: brand green is reserved for the price and CTA
              below, so the page reads title → featured price → button instead
              of three competing green focal points. */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.02em",
              mb: 1,
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              color: theme.palette.text.primary,
            }}
          >
            {isMembershipActive && "Upgrade to Pro"}
            {!isMembershipActive && "Choose your plan"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mx: "auto",
              maxWidth: 500,
              fontSize: { xs: "0.85rem", md: "0.95rem" },
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Checkout Error</AlertTitle>
            {error}
            {updatePaymentUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  href={updatePaymentUrl}
                  variant="outlined"
                  color="inherit"
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Update payment method
                </Button>
              </Box>
            )}
          </Alert>
        )}

        {/* Billing interval toggle (a member's plan change keeps their term) */}
        {!isMembershipActive && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            <ToggleButtonGroup
              value={interval}
              exclusive
              onChange={(_, next: BillingInterval | null) => {
                if (next) setInterval(next);
              }}
              size="small"
              sx={{
                // Segmented-control affordance: a visible hairline border with
                // the pills inset behind padding, so it reads as one pressable
                // toggle rather than two floating chips.
                p: 0.5,
                gap: 0.5,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : "rgba(0, 0, 0, 0.03)",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0, 0, 0, 0.15)"
                }`,
                borderRadius: 2.5,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  px: { xs: 2, sm: 2.5 },
                  border: "none",
                  borderRadius: "8px !important",
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : "rgba(0, 0, 0, 0.06)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="yearly">Annual</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {planEnding && membership && (
          <EndingPlanGuidance membership={membership} />
        )}

        {/* Pricing Section — two cards sized for two cards: the row is capped
            well below the header width so each card holds its content snugly
            instead of stretching a three-up design across half the screen. */}
        {!planEnding && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              maxWidth: 720,
              mx: "auto",
              mb: { xs: 2.5, md: 3 },
            }}
          >
            {PRICING_TIERS.map((tier) => {
              const isFeatured = tier.key === "pro";

              return (
                <PlanCard
                  key={tier.key}
                  title={tier.name}
                  subtitle={tier.tagline}
                  badge={isFeatured ? "Most Popular" : undefined}
                  badgeIcon={
                    isFeatured ? (
                      <StarIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />
                    ) : undefined
                  }
                  featured={isFeatured}
                  priceLabel={
                    shownInterval === "monthly"
                      ? formatPrice(tier.monthly)
                      : formatPrice(tier.yearlyPerMonth)
                  }
                  priceUnit="/month"
                  caption={
                    shownInterval === "monthly"
                      ? "Billed monthly"
                      : `Billed annually · ${formatPriceWhole(tier.yearly)}/year`
                  }
                  savingsChip={
                    shownInterval === "yearly" ? "2 months free" : undefined
                  }
                  features={tier.features}
                  ctaLabel={ctaLabelFor(tier)}
                  buttonVariant={isFeatured ? "contained" : "outlined"}
                  isLoading={loadingPlan === tier.key}
                  isRevalidating={isRevalidating}
                  onSelect={selectFor(tier)}
                />
              );
            })}
          </Box>
        )}

        {/* Currency note — small print, not a warning: it earns a quiet line
            under the cards, never an amber banner spanning the page. */}
        {!planEnding && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              mx: "auto",
              maxWidth: 420,
              mb: 2,
              color: "text.secondary",
              opacity: 0.75,
              fontSize: "0.7rem",
              lineHeight: 1.5,
            }}
          >
            Payments are processed in US Dollars (USD) by Lemon Squeezy. Your
            bank may apply a foreign currency conversion fee.
          </Typography>
        )}
      </Box>
      {isMembershipActive && membership && (
        <ChangePlanDialog
          membership={membership}
          target={upgradeTarget}
          onClose={() => setUpgradeTarget(null)}
          onChanged={handleUpgraded}
        />
      )}
    </Box>
  );
};
